import { useLocation } from '@tanstack/react-router'
import * as React from 'react'

const getPathAtDepth = (pathname: string, depth: number): string => {
  const segments = pathname.split('/').filter(Boolean)
  return segments.slice(0, depth).join('/')
}

interface RouteSuspenseProps {
  fallback: React.ReactNode
  children: React.ReactNode
  minMs?: number
  depth?: number
}

const RouteSuspense = ({
  fallback,
  children,
  minMs = 150,
  depth,
}: RouteSuspenseProps) => {
  const location = useLocation()

  const getComparePath = React.useCallback(
    (pathname: string): string => {
      return depth ? getPathAtDepth(pathname, depth) : pathname
    },
    [depth],
  )

  const [currentPath, setCurrentPath] = React.useState<string>(() =>
    getComparePath(location.pathname),
  )
  const [isTransitioning, setIsTransitioning] = React.useState<boolean>(false)

  React.useLayoutEffect(() => {
    const newPath = getComparePath(location.pathname)
    if (newPath !== currentPath) {
      setIsTransitioning(true)

      const timer = setTimeout(() => {
        setCurrentPath(newPath)
        setIsTransitioning(false)
      }, minMs)

      return () => clearTimeout(timer)
    }
  }, [location.pathname, currentPath, minMs, getComparePath])

  if (isTransitioning) {
    return fallback
  }

  return children
}
RouteSuspense.displayName = 'RouteSuspense'

export { RouteSuspense }
